import { Platform, Linking, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import versionConfig from '../config/version';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('updateChecker');
export interface UpdateInfo {
  available: boolean;
  latestVersion: string;
  currentVersion: string;
  forceUpdate: boolean; // Critical update that must be installed
  updateUrl?: string; // App Store or Play Store URL
  releaseNotes?: string;
  minimumVersion?: string; // Minimum version required by backend
}

interface VersionCheckResponse {
  latestVersion: string;
  minimumVersion: string;
  forceUpdate: boolean;
  releaseNotes: string;
  ios: {
    storeUrl: string;
  };
  android: {
    storeUrl: string;
  };
}

class UpdateCheckerService {
  private static LAST_CHECK_KEY = '@last_update_check';
  private static CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
  private static SKIP_VERSION_KEY = '@skip_version';

  // App Store URLs (fallbacks; the server's answer carries the current ones).
  private static IOS_APP_STORE_URL = 'https://apps.apple.com/app/id6756896149';
  // The Android applicationId is com.myfinalkeyapp (android/app/build.gradle);
  // until 2026-10-23 this pointed at com.myfinalkey, a different listing.
  private static ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.myfinalkeyapp';

  /**
   * Check for app updates from backend
   */
  async checkForUpdates(): Promise<UpdateInfo> {
    // The web app has no store and is always served the latest bundle; it
    // reports package.json's version (0.0.1), so a server minimum would
    // lock every web user out. Never ask on the web (the server also answers
    // "no update" to any browser).
    if (Platform.OS === 'web') {
      const currentVersion = (await versionConfig.getVersionInfo()).version;
      return { available: false, latestVersion: currentVersion, currentVersion, forceUpdate: false };
    }

    try {
      const currentVersion = (await versionConfig.getVersionInfo()).version;

      // Call your backend API to get latest version info
      const response = await fetch(`${API_URL}/version/check`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': await versionConfig.getUserAgent(),
        },
      });

      if (!response.ok) {
        // Fallback: No update available if API fails
        return {
          available: false,
          latestVersion: currentVersion,
          currentVersion,
          forceUpdate: false,
        };
      }

      const data: VersionCheckResponse = await response.json();

      const isUpdateAvailable = versionConfig.isUpdateAvailable(
        currentVersion,
        data.latestVersion
      );

      const meetsMinimum = await versionConfig.meetsMinimumVersion(
        data.minimumVersion
      );

      return {
        available: isUpdateAvailable,
        latestVersion: data.latestVersion,
        currentVersion,
        forceUpdate: data.forceUpdate || !meetsMinimum,
        updateUrl:
          Platform.OS === 'ios' ? data.ios.storeUrl : data.android.storeUrl,
        releaseNotes: data.releaseNotes,
        minimumVersion: data.minimumVersion,
      };
    } catch (error) {
      logger.error('Failed to check for updates:', error);

      // Fallback: No update available
      const currentVersion = (await versionConfig.getVersionInfo()).version;
      return {
        available: false,
        latestVersion: currentVersion,
        currentVersion,
        forceUpdate: false,
      };
    }
  }

  /**
   * Check if update check is due (respects check interval)
   */
  async shouldCheckForUpdates(): Promise<boolean> {
    try {
      const lastCheckStr = await AsyncStorage.getItem(
        UpdateCheckerService.LAST_CHECK_KEY
      );

      if (!lastCheckStr) {
        return true;
      }

      const lastCheck = parseInt(lastCheckStr, 10);
      const now = Date.now();

      return now - lastCheck > UpdateCheckerService.CHECK_INTERVAL;
    } catch (error) {
      logger.error('Failed to check update interval:', error);
      return true;
    }
  }

  /**
   * Record that an update check was performed
   */
  async recordUpdateCheck(): Promise<void> {
    try {
      await AsyncStorage.setItem(
        UpdateCheckerService.LAST_CHECK_KEY,
        Date.now().toString()
      );
    } catch (error) {
      logger.error('Failed to record update check:', error);
    }
  }

  /**
   * Check for updates and show alert if available
   */
  async checkAndNotify(forceCheck: boolean = false): Promise<void> {
    try {
      // Check if we should skip this version
      if (!forceCheck) {
        const shouldCheck = await this.shouldCheckForUpdates();
        if (!shouldCheck) {
          return;
        }
      }

      const updateInfo = await this.checkForUpdates();
      await this.recordUpdateCheck();

      if (!updateInfo.available) {
        if (forceCheck) {
          Alert.alert(
            'You\'re Up to Date',
            `You have the latest version (${updateInfo.currentVersion}) installed.`
          );
        }
        return;
      }

      // Check if user chose to skip this version
      if (!forceCheck && !updateInfo.forceUpdate) {
        const skippedVersion = await this.getSkippedVersion();
        if (skippedVersion === updateInfo.latestVersion) {
          return;
        }
      }

      this.showUpdateAlert(updateInfo, forceCheck);
    } catch (error) {
      logger.error('Failed to check and notify:', error);
    }
  }

  /**
   * Show update alert to user
   */
  private showUpdateAlert(updateInfo: UpdateInfo, forceCheck: boolean): void {
    const title = updateInfo.forceUpdate
      ? 'Update Required'
      : 'Update Available';

    const message = updateInfo.forceUpdate
      ? `A critical update is required to continue using MyFinalKey. Please update to version ${updateInfo.latestVersion}.`
      : `Version ${updateInfo.latestVersion} is now available. You are currently on version ${updateInfo.currentVersion}.\n\n${updateInfo.releaseNotes || ''}`;

    const buttons = updateInfo.forceUpdate
      ? [
          {
            text: 'Update Now',
            onPress: () => this.openStoreUpdate(updateInfo.updateUrl),
          },
        ]
      : [
          {
            text: 'Skip',
            style: 'cancel' as const,
            onPress: () => this.skipVersion(updateInfo.latestVersion),
          },
          {
            text: 'Later',
            style: 'cancel' as const,
          },
          {
            text: 'Update',
            onPress: () => this.openStoreUpdate(updateInfo.updateUrl),
          },
        ];

    Alert.alert(title, message, buttons, {
      cancelable: !updateInfo.forceUpdate,
    });
  }

  /**
   * Open App Store or Play Store for update
   */
  private async openStoreUpdate(customUrl?: string): Promise<void> {
    const url =
      customUrl ||
      (Platform.OS === 'ios'
        ? UpdateCheckerService.IOS_APP_STORE_URL
        : UpdateCheckerService.ANDROID_PLAY_STORE_URL);

    try {
      const supported = await Linking.canOpenURL(url);

      if (supported) {
        await Linking.openURL(url);
      } else {
        Alert.alert('Error', 'Unable to open the app store.');
      }
    } catch (error) {
      logger.error('Failed to open store:', error);
      Alert.alert('Error', 'Unable to open the app store.');
    }
  }

  /**
   * Mark a version as skipped
   */
  private async skipVersion(version: string): Promise<void> {
    try {
      await AsyncStorage.setItem(UpdateCheckerService.SKIP_VERSION_KEY, version);
    } catch (error) {
      logger.error('Failed to skip version:', error);
    }
  }

  /**
   * Get skipped version
   */
  private async getSkippedVersion(): Promise<string | null> {
    try {
      return await AsyncStorage.getItem(UpdateCheckerService.SKIP_VERSION_KEY);
    } catch (error) {
      logger.error('Failed to get skipped version:', error);
      return null;
    }
  }

  /**
   * Clear skipped version
   */
  async clearSkippedVersion(): Promise<void> {
    try {
      await AsyncStorage.removeItem(UpdateCheckerService.SKIP_VERSION_KEY);
    } catch (error) {
      logger.error('Failed to clear skipped version:', error);
    }
  }

  /**
   * Force check for updates (ignores interval and skipped versions)
   */
  async forceCheckForUpdates(): Promise<void> {
    await this.clearSkippedVersion();
    await this.checkAndNotify(true);
  }

  /**
   * Check if app version is compatible with backend
   */
  async isCompatibleWithBackend(): Promise<boolean> {
    try {
      const updateInfo = await this.checkForUpdates();

      if (updateInfo.minimumVersion) {
        return await versionConfig.meetsMinimumVersion(
          updateInfo.minimumVersion
        );
      }

      return true;
    } catch (error) {
      logger.error('Failed to check backend compatibility:', error);
      return true; // Assume compatible if check fails
    }
  }

  /**
   * Get update status without showing alert
   */
  async getUpdateStatus(): Promise<UpdateInfo> {
    return await this.checkForUpdates();
  }
}

export default new UpdateCheckerService();
